"use client"

import { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import {
  X,
  Clock,
  ShoppingBag,
  Download,
  Eye,
  Calendar,
  Search,
  ChevronDown,
  Receipt,
  CheckCircle,
  AlertCircle,
  Package,
} from "lucide-react"
import { useApp } from "@/lib/context"
import { generateInvoicePDF } from "@/lib/invoice-generator"

interface OrderHistoryModalProps {
  isOpen: boolean
  onClose: () => void
}

type FilterStatus = "all" | "Pendente" | "preparando" | "Entregue" | "Cancelado"

export default function OrderHistoryModal({ isOpen, onClose }: OrderHistoryModalProps) {
  const { orderHistory, getCompletedOrdersHistory, customerAuth } = useApp()
  const [searchTerm, setSearchTerm] = useState("")
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("all")
  const [selectedOrder, setSelectedOrder] = useState<any>(null)
  const [showOrderDetail, setShowOrderDetail] = useState(false)

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("pt-AO", {
      style: "currency",
      currency: "AOA",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("pt-AO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
  }

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString("pt-AO", {
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case "entregue":
      case "finalizado":
        return <CheckCircle className="w-4 h-4 text-green-600" />
      case "preparando":
      case "em preparo":
        return <Package className="w-4 h-4 text-blue-600" />
      case "cancelado":
        return <AlertCircle className="w-4 h-4 text-red-600" />
      default:
        return <Clock className="w-4 h-4 text-yellow-600" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "entregue":
      case "finalizado":
        return "bg-green-100 text-green-800 border-green-200"
      case "preparando":
      case "em preparo":
        return "bg-blue-100 text-blue-800 border-blue-200"
      case "cancelado":
        return "bg-red-100 text-red-800 border-red-200"
      default:
        return "bg-yellow-100 text-yellow-800 border-yellow-200"
    }
  }

  // Filtrar pedidos
  const filteredOrders = orderHistory.filter((order) => {
    const matchesSearch =
      order.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customerName.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = filterStatus === "all" || order.status === filterStatus
    return matchesSearch && matchesStatus
  })

  const handleDownloadPDF = (order: any) => {
    try {
      // Reconstruir estrutura do pedido para o PDF
      const orderForPDF = {
        _id: order.orderId,
        createdAt: order.date,
        status: order.status,
        itens: order.items.map((item: any) => ({
          prato: {
            nome: item.prato.nome,
            preco: item.prato.preco,
            descricao: item.prato.descricao || "",
          },
          quantidade: item.quantidade,
        })),
      }

      generateInvoicePDF({
        order: orderForPDF,
        customerName: order.customerName,
        customerPhone: customerAuth?.phoneNumber || "",
        paymentMethod: "Dinheiro", // Pode ser melhorado para armazenar o método real
      })
    } catch (error) {
      console.error("Erro ao gerar PDF:", error)
    }
  }

  const handleViewOrder = (order: any) => {
    setSelectedOrder(order)
    setShowOrderDetail(true)
  }

  const handleCloseDetail = () => {
    setShowOrderDetail(false)
    setSelectedOrder(null)
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="bg-background text-foreground rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border bg-primary text-primary-foreground shrink-0">
            <div className="flex items-center gap-3">
              <Receipt className="w-6 h-6" />
              <div>
                <h2 className="text-xl font-bold">Histórico de Pedidos</h2>
                <p className="text-primary-foreground/80 text-sm">
                  {filteredOrders.length} {filteredOrders.length === 1 ? "pedido" : "pedidos"}
                </p>
              </div>
            </div>

            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-primary-foreground/20 hover:bg-primary-foreground/30 transition-colors flex items-center justify-center"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Filters */}
          <div className="p-6 border-b border-border bg-muted shrink-0">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Buscar por número do pedido ou nome..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent"
                />
              </div>

              {/* Status Filter */}
              <div className="relative">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value as FilterStatus)}
                  className="appearance-none bg-background border border-input rounded-lg px-4 py-2 pr-8 focus:ring-2 focus:ring-ring focus:border-transparent"
                >
                  <option value="all">Todos os Status</option>
                  <option value="Pendente">Pendente</option>
                  <option value="preparando">Em Preparo</option>
                  <option value="Entregue">Entregue</option>
                  <option value="Cancelado">Cancelado</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Orders List */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            {filteredOrders.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mb-4">
                  <Receipt className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {searchTerm || filterStatus !== "all" ? "Nenhum pedido encontrado" : "Nenhum pedido ainda"}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {searchTerm || filterStatus !== "all"
                    ? "Tente ajustar os filtros de busca"
                    : "Seus pedidos aparecerão aqui após serem finalizados"}
                </p>
              </div>
            ) : (
              <div className="p-6 space-y-4">
                {filteredOrders.map((order, index) => (
                  <motion.div
                    key={order.orderId}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-card border border-border rounded-xl p-4 hover:shadow-md transition-all duration-300"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                          <ShoppingBag className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground">Pedido #{order.orderId.slice(-6)}</h3>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Calendar className="w-3 h-3" />
                            <span>
                              {formatDate(order.date)} às {formatTime(order.date)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${getStatusColor(order.status)}`}
                        >
                          {getStatusIcon(order.status)}
                          {order.status}
                        </span>
                      </div>
                    </div>

                    {/* Items Preview */}
                    <div className="mb-3">
                      <div className="text-sm text-muted-foreground mb-1">
                        {order.items.length} {order.items.length === 1 ? "item" : "itens"}:
                      </div>
                      <div className="text-sm text-foreground">
                        {order.items.slice(0, 2).map((item: any, idx: number) => (
                          <span key={idx}>
                            {item.quantidade}x {item.prato.nome}
                            {idx < Math.min(order.items.length, 2) - 1 && ", "}
                          </span>
                        ))}
                        {order.items.length > 2 && (
                          <span className="text-muted-foreground"> +{order.items.length - 2} mais</span>
                        )}
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-3 border-t border-border">
                      <div className="font-bold text-primary text-lg">{formatPrice(order.total)}</div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewOrder(order)}
                          className="px-3 py-1.5 text-sm bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors flex items-center gap-1"
                        >
                          <Eye className="w-3 h-3" />
                          Ver
                        </button>
                        <button
                          onClick={() => handleDownloadPDF(order)}
                          className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-colors flex items-center gap-1"
                        >
                          <Download className="w-3 h-3" />
                          PDF
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </motion.div>

        {/* Order Detail Modal */}
        <AnimatePresence>
          {showOrderDetail && selectedOrder && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-60 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto"
              onClick={handleCloseDetail}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-background text-foreground rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Detail Header */}
                <div className="flex items-center justify-between p-6 border-b border-border shrink-0">
                  <div>
                    <h3 className="text-xl font-bold text-foreground">Pedido #{selectedOrder.orderId.slice(-6)}</h3>
                    <p className="text-sm text-muted-foreground">
                      {formatDate(selectedOrder.date)} às {formatTime(selectedOrder.date)}
                    </p>
                  </div>
                  <button
                    onClick={handleCloseDetail}
                    className="w-8 h-8 rounded-full bg-muted hover:bg-muted/80 transition-colors flex items-center justify-center"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Detail Content */}
                <div className="p-6 overflow-y-auto flex-1 min-h-0">
                  {/* Status */}
                  <div className="mb-6">
                    <div
                      className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium border ${getStatusColor(selectedOrder.status)}`}
                    >
                      {getStatusIcon(selectedOrder.status)}
                      Status: {selectedOrder.status}
                    </div>
                  </div>

                  {/* Items */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-foreground mb-3">Itens do Pedido</h4>
                    <div className="space-y-3">
                      {selectedOrder.items.map((item: any, index: number) => (
                        <div key={index} className="flex justify-between items-center p-3 bg-muted rounded-lg">
                          <div className="flex-1">
                            <div className="font-medium text-foreground">
                              {item.quantidade}x {item.prato.nome}
                            </div>
                            <div className="text-sm text-muted-foreground">{formatPrice(item.prato.preco)} cada</div>
                          </div>
                          <div className="font-semibold text-primary">
                            {formatPrice(item.prato.preco * item.quantidade)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Total */}
                  <div className="border-t pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-foreground">Total</span>
                      <span className="text-2xl font-bold text-primary">{formatPrice(selectedOrder.total)}</span>
                    </div>
                  </div>
                </div>

                {/* Detail Footer */}
                <div className="p-6 border-t border-border shrink-0">
                  <button
                    onClick={() => handleDownloadPDF(selectedOrder)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-xl hover:opacity-90 transition-all duration-300"
                  >
                    <Download className="w-4 h-4" />
                    Baixar Fatura PDF
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  )
}
